import { tokens } from '@news-mono/design-tokens'
import {
    AppState,
    AuthenticationState,
    BreachEvent,
    BreachScreenType,
    DataLayerEventName,
    decompileSswCartPayload,
    RemoveFromCartEvent,
    SubscriptionPromptCheckoutEvent,
    SubscriptionPromptCookieData,
    ViewCartEvent,
} from '@news-mono/web-common'
import debug from 'debug'
import Cookies from 'js-cookie'
import React from 'react'
import { useSelector } from 'react-redux'
import { usePromptClickHandler } from '../../../../__helpers/prompt-click-helper'
import { TheWestMark } from '../../../../logos/TheWestMark/TheWestMark'
import { TheWestHeaderCloseIcon } from '../../../../navigation/TheWestHeaderDropdown/icons/TheWestHeaderCloseIcon'
import { useAuthActions } from '../../../authentication'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { MiniSubscriptionPackage } from '../SubscriptionPackage/MiniSubscriptionPackage'
import { StyledSubscriptionPackageButton2022 } from '../SubscriptionPackage/SubscriptionPackage.styled'
import {
    StyledSubscriptionPrompt,
    StyledSubscriptionPromptButtonWrapper,
    StyledSubscriptionPromptCloseButton,
    StyledSubscriptionPromptMenuBar,
    StyledSubscriptionPromptSecondaryButton,
} from './SubscriptionPrompt.styled'

export const SUBSCRIPTION_CART_PROMPT_ID = 'subscription-cart-prompt'
const subscriptionCartDebug = debug('subscription-cart-prompt')
const DEFAULT_IMAGE = require('../SubscriptionPackage/images/updated2022/redesign/everyday_digital_cropped.webp')

export const SubscriptionPrompt = ({
    promptData,
    onEvent,
    refreshCart,
}: {
    promptData: SubscriptionPromptCookieData
    onEvent: (event: BreachEvent) => void
    refreshCart: () => void
}) => {
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const isEntitled = React.useCallback(
        () => authentication.isLoggedIn && authentication.isEntitled === true,
        [authentication],
    )

    const [hasAlreadyPrompted, setHasAlreadyPrompted] = React.useState(false)
    const { onSubscribeClick } = useAuthActions()
    const decompiledCartPayload = React.useMemo(
        () => decompileSswCartPayload(promptData),
        [promptData],
    )

    const { isHidden, backgroundComponentRef, hide, show } =
        usePromptClickHandler({
            promptId: SUBSCRIPTION_CART_PROMPT_ID,
            type: 'cookies',
            debug: subscriptionCartDebug,
            showPromptAfterInactivityOfMs: hasAlreadyPrompted
                ? undefined
                : 5 * 60 * 1000, // 5 minutes
            onPromptHide: () => {
                subscriptionCartDebug(
                    'Hiding the subscription prompt to the user.',
                )

                setHasAlreadyPrompted(true)
                if (decompiledCartPayload) {
                    onViewPageChange(
                        onEvent,
                        false,
                        decompiledCartPayload?.cartPayload as ViewCartEvent['payload'],
                    )
                }
            },
            onPromptShow: () => {
                subscriptionCartDebug(
                    'Showing the subscription prompt to the user.',
                )

                if (decompiledCartPayload) {
                    onViewPageChange(
                        onEvent,
                        true,
                        decompiledCartPayload?.cartPayload as ViewCartEvent['payload'],
                    )
                }
            },
        })

    const navigateAwayHandler = React.useCallback(
        (e: BeforeUnloadEvent) => {
            if (!hasAlreadyPrompted) {
                setTimeout(show, 10)
                e.preventDefault()
                e.returnValue =
                    'You have an incomplete subscription. Are you sure you wish to leave without completing it?'
                return e.returnValue
            }
        },
        [hasAlreadyPrompted, show],
    )

    // Handle when the user tries to navigate off the tab
    React.useEffect(() => {
        if (isEntitled()) {
            return
        }

        if (isHidden) {
            window.addEventListener('beforeunload', navigateAwayHandler)
        } else {
            window.removeEventListener('beforeunload', navigateAwayHandler)
        }
        return () => {
            window.removeEventListener('beforeunload', navigateAwayHandler)
        }
    }, [isEntitled, isHidden, navigateAwayHandler])

    return isHidden || isEntitled() || !decompiledCartPayload ? null : (
        <StyledSubscriptionPrompt
            ref={backgroundComponentRef}
            hidden={isHidden}
            aria-hidden={isHidden}
        >
            <div>
                <StyledSubscriptionPromptMenuBar>
                    <TheWestMark height={36} />
                    <StyledSubscriptionPromptCloseButton onClick={hide}>
                        <TheWestHeaderCloseIcon
                            fill={tokens.thewest.colors.palette.greyQuokka}
                        />
                    </StyledSubscriptionPromptCloseButton>
                </StyledSubscriptionPromptMenuBar>
                <h2>Are you still there?</h2>
                <p>We've saved your subscription progress.</p>
                <MiniSubscriptionPackage
                    subscriptionPackage={
                        decompiledCartPayload.subscriptionPackage
                    }
                    pricePackage={decompiledCartPayload.pricePackage}
                    imagePath={decompiledCartPayload.imagePath || DEFAULT_IMAGE}
                />
                <StyledSubscriptionPromptButtonWrapper>
                    <StyledSubscriptionPackageButton2022
                        packageOptions={{
                            theme: 'default',
                        }}
                        breachOptions={{
                            breachType: 'default',
                        }}
                        style={{ maxWidth: 252 }}
                        onClick={() => {
                            const breachScreenType: BreachScreenType =
                                'Premium - Inline'
                            const coupon =
                                decompiledCartPayload.successful &&
                                decompiledCartPayload.cartPayload.items
                                    ?.length > 0
                                    ? decompiledCartPayload.cartPayload
                                          ?.items[0].coupon ?? ''
                                    : ''

                            // Link click event
                            createLinkClickEvent(
                                'SubscriptionPrompt_Subscribe_Click',
                                coupon,
                                onEvent,
                                breachScreenType,
                                1,
                            )()
                            // Send subscription_prompt_checkout event
                            onEvent({
                                type: DataLayerEventName.subscriptionPromptCheckout,
                                originator: 'subscriptionprompt',
                                payload:
                                    decompiledCartPayload.cartPayload as SubscriptionPromptCheckoutEvent['payload'],
                            })

                            onSubscribeClick({
                                packagePath:
                                    decompiledCartPayload.pricePackage
                                        .packagePath ?? undefined,
                                offerCode: coupon,
                                callToAction: 'shopping_cart',
                                breachScreenType: breachScreenType,
                            })
                        }}
                    >
                        Checkout
                    </StyledSubscriptionPackageButton2022>
                    <StyledSubscriptionPromptSecondaryButton
                        packageOptions={{
                            theme: 'default',
                        }}
                        breachOptions={{
                            breachType: 'default',
                        }}
                        style={{ maxWidth: 252 }}
                        onClick={() => {
                            if (
                                decompiledCartPayload.successful &&
                                decompiledCartPayload.cartPayload.items
                                    ?.length > 0
                            ) {
                                // Link click event
                                createLinkClickEvent(
                                    'SubscriptionPrompt_Clear_Cart_Click',
                                    decompiledCartPayload.cartPayload.items[0]
                                        .coupon || '',
                                    onEvent,
                                    undefined,
                                    1,
                                )()
                                // Send remove from cart event
                                onEvent({
                                    type: DataLayerEventName.removeFromCart,
                                    originator: 'subscriptionprompt',
                                    payload:
                                        decompiledCartPayload.cartPayload as RemoveFromCartEvent['payload'],
                                })
                            }

                            handleCookieClear()
                            hide()
                            refreshCart()
                        }}
                    >
                        Clear cart
                    </StyledSubscriptionPromptSecondaryButton>
                </StyledSubscriptionPromptButtonWrapper>
            </div>
        </StyledSubscriptionPrompt>
    )
}

function onViewPageChange(
    onEvent: (event: BreachEvent) => void,
    viewing: boolean,
    payloadInfo: ViewCartEvent['payload'],
) {
    if (viewing) {
        onEvent({
            type: DataLayerEventName.viewCart,
            originator: 'breachscreen',
            payload: payloadInfo,
        })
    }
}

function handleCookieClear() {
    // Does cookie exist? If so set it's expiry 14 days ago so it's not considered an
    // active cookie.
    // This is done instead of using Cookies.remove() so that we don't run into issues
    // with it not clearing due to domain permission issues.
    const cookie = Cookies.get('subscribeCart')
    if (cookie) {
        const cookieOptions = {
            expires: -14,
            path: '/', // available on all paths.
            domain: getTopCookieDomain(),
        }

        Cookies.set('subscribeCart', cookie, cookieOptions)
    }
}

// Copied from SSW @ 2023-06-30_15-53 AWST
function getTopCookieDomain() {
    const TEMP_COOKIE_NAME = 'check-top-domain'
    const domainSegments = location.hostname.split('.').reverse()
    let domain = ''
    let options = {}
    do {
        domain = domainSegments.splice(0, 1)[0] + domain
        if (domainSegments.length > 0) {
            domain = '.' + domain
        }

        options = { domain: domain }
        Cookies.set(TEMP_COOKIE_NAME, 'ok', options)
    } while (
        domainSegments.length > 0 &&
        Cookies.get(TEMP_COOKIE_NAME) !== 'ok'
    )

    Cookies.remove(TEMP_COOKIE_NAME, options)

    // We want to make sure that on prod the news-mono has access
    // to remove the cart too.
    if (domain.startsWith('.')) {
        domain = domain.substring(1)
    }

    return domain
}
